import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getDefaultValue } from '@pages/EntityCreateOrEdit/components/AddColumnDialog/helpers/getDefaultValue'
import { transformTextParamsData } from '@pages/Parameters/helpers'

import { GetTextParamsHandle } from '@components/TextSettings'

import {
  useFetchAllObjectQuery,
  useFetchObjectByCodeQuery,
  useFetchViewColumnByIdQuery,
} from '@redux/api'
import { useFetchAllViewQuery } from '@redux/api/view.api'

import { usePrompt } from '@hooks'
import {
  createAutocompleteOptions,
  getColumnParametersFromArray,
  getEntityTypeForRequest,
  isObjectValueType,
} from '@helpers'
import {
  BIND_TYPE,
  ENTITY,
  EVENT_CODE,
  FIELD_VALUE_FORMAT,
  FIELD_VALUE_TYPE,
  MODEL_TYPE,
  OBJECT_FIELD_TYPE,
  PREFILL_OPTIONS,
} from '@constants'
import { ObjectFieldDTO, ObjectFieldWithUsed } from '@types'

import { AddColumnDialogType } from '../AddColumnDialog'

import { useHandlers } from './useHandlers'
import { ColumnForm, useInputs } from './useInputs'

export type ObjectValueOptionsType = {
  id: number
  name: string
  isAlreadyUsed: boolean
  fieldType: OBJECT_FIELD_TYPE
  isPk?: boolean
  isObjectLevelRequired?: boolean
}

export const useAddColumnDialog = (props: AddColumnDialogType) => {
  const {
    rawObjectFields,
    currentRow,
    columns,
    objectCode: viewObjectCode,
    objectCommands = [],
    bindingValuesColumns,
    onSetModalProperty,
    entityFormCode,
    type,
    actionTable,
    entityType,
  } = props

  const defaultValues: ColumnForm = getDefaultValue({ type })
  const methods = useForm<ColumnForm>({ defaultValues })
  const columnHeaderSettingsRef = useRef<GetTextParamsHandle>(null)
  const columnTextSettingsRef = useRef<GetTextParamsHandle>(null)
  const [isLoading, setLoading] = useState(type === MODEL_TYPE.VIEW)
  const [objectField, setObjectField] = useState<ObjectFieldDTO>()
  const embeddedObjectCode = objectField?.model ?? ''

  const {
    handleSubmit,
    reset,
    formState: { isDirty: isDirtyBasicParameters, errors },
    getValues,
    setValue,
    clearErrors,
    trigger,
    watch,
  } = methods

  const isJsJsxBinding = useRef(false)

  const watchBinding = watch('bindType')
  const watchObjectValue = watch('objectValue')
  const watchValue = watch('value')
  const watchUseDisplayRule = watch('useDisplayRule')
  const watchDisplayRule = watch('displayRule')
  const watchUseParameters = watch('useParameters')
  const watchAsCheckbox = watch('asCheckbox')
  const watchAsDuration = watch('asDuration')
  const watchPathArray = watch('pathArray')
  const watchDisableEditable = watch('disableEditable')
  const watchActions = watch('actions')
  const watchedDropDownListFilters = watch('dropDownListFilters')
  const watchedDropDownWindowFilters = watch('dropDownWindowFilters')

  const fieldType = objectField?.type ?? null
  const valueType = objectField?.valueType || null
  const objectCode = objectField?.model ?? ''
  const isObjectLinkedValue = isObjectValueType(objectField?.valueType)

  const { data: objectForField } = useFetchObjectByCodeQuery(objectCode, { skip: !objectCode })

  const { headerProperties, textProperties } = getColumnParametersFromArray(
    currentRow ? currentRow.parameters : []
  )

  const { data: dropdownEntities } = useFetchAllViewQuery(
    {
      objectCode: embeddedObjectCode,
      viewType: getEntityTypeForRequest(ENTITY.DROP_DOWN_ENTITY),
    },
    { refetchOnMountOrArgChange: true, skip: !embeddedObjectCode }
  )

  const { data: columnDDE, isLoading: isLoadingColumnData } = useFetchViewColumnByIdQuery(
    currentRow?.id,
    { skip: !currentRow?.id || type !== MODEL_TYPE.VIEW }
  )

  const { data: objects } = useFetchAllObjectQuery()

  const initialColumnHeaderStyles = useMemo(() => {
    return transformTextParamsData(headerProperties)
  }, [currentRow])
  const initialColumnTextStyles = useMemo(() => {
    return transformTextParamsData(textProperties)
  }, [currentRow])

  const filterObjectValueOptions = useCallback(
    (fields: ObjectFieldDTO[]) =>
      fields.map(field => {
        const isAlreadyUsed = Boolean(
          columns?.find(column => {
            return column?.value === watchValue && column.objectValue === field.name
          })
        )

        return { id: field.id, name: field.name, isAlreadyUsed }
      }),
    [columns, watchValue, currentRow]
  )

  const requiredFields = useMemo(
    () => new Set(rawObjectFields?.filter(field => field.required).map(field => field.name)),
    [rawObjectFields]
  )

  const isFieldRequired = useMemo(
    () => requiredFields.has(watchValue),
    [requiredFields, watchValue]
  )

  const objectValueOptions = useMemo(
    () => filterObjectValueOptions(objectForField?.fields || []),
    [filterObjectValueOptions, objectForField?.fields]
  )

  const columnAvailableEvents = new Set([
    EVENT_CODE.ON_ROW_EDIT,
    EVENT_CODE.ON_ROW_DELETE_BUTTON_CLICK,
    EVENT_CODE.ON_ROW_CLICK,
    EVENT_CODE.ON_ROW_DOWNLOAD_BUTTON_CLICK,
    EVENT_CODE.ON_ROW_PREVIEW_BUTTON_CLICK,
    EVENT_CODE.ON_ROW_ADDITIONAL_BUTTON_CLICK,
  ])

  const columnActions = actionTable.rows.filter(action =>
    columnAvailableEvents.has(action?.eventCode as EVENT_CODE)
  )

  const actionEventsOptions = useMemo(
    () => createAutocompleteOptions(columnActions, { id: 'id', label: 'title' }),
    [columnActions]
  )

  const commandOptions = useMemo(
    () => createAutocompleteOptions(objectCommands, { id: 'code', label: 'code' }),
    [objectCommands]
  )

  const preFill = currentRow?.preFill
  const preFillRootObjectCode = preFill ? preFill?.preFillLink[0].objectCode : null
  const preFillFilterNameObjectCode = preFill?.preFillFilterName?.length
    ? preFill?.preFillFilterName[0].objectCode
    : null

  const preFillRootObject = preFillRootObjectCode
    ? objects?.find(object => object.code === preFillRootObjectCode)
    : null
  const preFillFilterNameObject = preFillFilterNameObjectCode
    ? objects?.find(object => object.code === preFillFilterNameObjectCode)
    : null

  const filterObjectFields = (fields?: ObjectFieldDTO[]): ObjectFieldWithUsed[] =>
    fields?.map(field => {
      const isAlreadyUsed = Boolean(columns?.find(column => column.value === field.name))

      return {
        ...field,
        isAlreadyUsed,
      }
    }) || []

  const mappedActions =
    actionTable.rows?.filter(row =>
      currentRow?.actions?.some(action => action.code === row.code)
    ) || []

  const actionEventsSelectedOptions = useMemo(
    () => createAutocompleteOptions(mappedActions, { id: 'id', label: 'title' }),
    [mappedActions]
  )

  const filteredObjectFields = useMemo(
    () => filterObjectFields(rawObjectFields),
    [rawObjectFields, columns, objectValueOptions]
  )

  const valueOptions: ObjectValueOptionsType[] = useMemo(
    () =>
      filteredObjectFields.map(({ id, name, isAlreadyUsed, type, isPk }) => ({
        id,
        name,
        isAlreadyUsed,
        fieldType: type,
        isPk,
      })),
    [filteredObjectFields]
  )

  const bindTypeOptions = useMemo(
    () =>
      bindingValuesColumns
        ?.map(cat => ({
          id: cat.id,
          name: cat.code,
        }))
        .filter(
          cat =>
            !(cat.name === BIND_TYPE.FIELD && valueOptions.length === 0) &&
            !(entityType === ENTITY.DROP_DOWN_ENTITY && cat.name === BIND_TYPE.ACTION)
        ) || [],
    [bindingValuesColumns, valueOptions]
  )

  // TODO дубликат
  useEffect(() => {
    const field = rawObjectFields?.find(field => field.name === watchValue)
    setObjectField(field ? field : undefined)
    clearErrors()

    if (isFieldRequired) {
      setValue('disableEditable', false)
    }

    if (currentRow && field && currentRow?.value === field?.name) {
      setValue('objectValue', currentRow?.objectValue)

      return
    }
  }, [rawObjectFields, watchValue, currentRow, objectCode])

  // Когда поля asСheckbox и asDuration скрываются, нужно ресетить их значения, чтобы случайно не отправилось true
  // при иных valueType
  useEffect(() => {
    if (objectField?.type === OBJECT_FIELD_TYPE.ENUM) {
      return
    }

    if (valueType !== FIELD_VALUE_TYPE.INTEGER && valueType !== FIELD_VALUE_TYPE.BOOLEAN) {
      if (watchAsCheckbox && isDirty) {
        setValue('asCheckbox', false)
      }
    }

    if (valueType !== FIELD_VALUE_TYPE.INTEGER) {
      if (watchAsDuration && isDirty) {
        setValue('asDuration', false)
      }
    }
  }, [valueType])

  useEffect(() => {
    if (currentRow && ((type === MODEL_TYPE.VIEW && columnDDE) || type !== MODEL_TYPE.VIEW)) {
      reset({
        ...currentRow,
        valueType,
        actions: actionEventsSelectedOptions,
        order: currentRow.order?.toString(),
        columnToSort: currentRow.columnToSort ? { id: 0, label: currentRow.columnToSort } : null,
        pinnedColumn: currentRow.pinnedColumn
          ? { id: currentRow.pinnedColumn, label: currentRow.pinnedColumn }
          : null,
        useParameters: Boolean(currentRow?.useLocalParameters),
        useDisplayRule: currentRow?.useDisplayRule,
        disableEditable: !currentRow?.editable,
        dropDownListCode: columnDDE?.dropDownList
          ? {
              id: columnDDE.dropDownList.code,
              label: columnDDE.dropDownList.title,
            }
          : null,
        dropDownWindowCode: columnDDE?.dropDownWindow
          ? {
              id: columnDDE.dropDownWindow.code,
              label: columnDDE.dropDownWindow.title,
            }
          : null,
        dropDownListFilters: columnDDE?.dropDownListFilters || [],
        dropDownWindowFilters: columnDDE?.dropDownWindowFilters || [],
        preFillType: preFill
          ? {
              id: preFill.preFillType === PREFILL_OPTIONS.DYNAMIC ? 0 : 1,
              option: preFill.preFillType,
              label: PREFILL_OPTIONS[preFill.preFillType],
            }
          : null,
        preFillSourceObject: preFillRootObject
          ? { id: preFillRootObject.code, label: preFillRootObject.title }
          : null,
        preFillFilterObject: preFillFilterNameObject
          ? { id: preFillFilterNameObject.code, label: preFillFilterNameObject.title }
          : null,
        preFillLink: preFill?.preFillLink,
        preFillFilterName: preFill?.preFillFilterName,
        preFillLinkStr: preFill?.preFillLinkStr,
        preFillFilterNameStr: preFill?.preFillFilterNameStr,
        preFillStaticValue: preFill?.value || '',
        displayRule: currentRow?.displayRuleId
          ? { id: currentRow?.displayRuleId, label: currentRow?.displayRuleTitle }
          : null,
        asCheckbox:
          currentRow.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
          currentRow.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN,
        asDuration: currentRow.valueFormat === FIELD_VALUE_FORMAT.TIME,
        formatDate: currentRow.formatDate
          ? { id: currentRow.formatDate, label: currentRow.formatDate }
          : null,
      })

      setLoading(false)
    }
  }, [currentRow, columnDDE])

  useEffect(() => {
    if (columnDDE) {
      setValue('dropDownListFilters', columnDDE.dropDownListFilters || [], { shouldDirty: false })
      setValue('dropDownWindowFilters', columnDDE.dropDownWindowFilters || [], {
        shouldDirty: false,
      })
      setValue(
        'dropDownListCode',
        columnDDE?.dropDownList
          ? {
              id: columnDDE.dropDownList.code,
              label: columnDDE.dropDownList.title,
            }
          : null,
        { shouldDirty: false }
      )
      setValue(
        'dropDownWindowCode',
        columnDDE?.dropDownWindow
          ? {
              id: columnDDE.dropDownWindow.code,
              label: columnDDE.dropDownWindow.title,
            }
          : null,
        { shouldDirty: false }
      )
    }
  }, [columnDDE, setValue])

  useEffect(() => {
    onSetModalProperty?.(MODEL_TYPE.COLUMN)
  }, [])

  useEffect(() => {
    if (watchBinding === BIND_TYPE.JS || watchBinding === BIND_TYPE.JSX) {
      const value =
        currentRow?.value &&
        (currentRow.bindType === BIND_TYPE.JS || currentRow?.bindType === BIND_TYPE.JSX)
          ? currentRow.value
          : ''
      isJsJsxBinding.current = true
      setValue('value', value)
    }
    if (watchBinding === BIND_TYPE.FIELD && isJsJsxBinding.current) {
      setValue('objectValue', '')
      setValue('pathArray', [])
      setValue('value', '')
      isJsJsxBinding.current = false
    }
  }, [watchBinding])

  const { state, handlers, data } = useHandlers({
    objectField,
    entityFormCode,
    watchBinding,
    watchUseParameters,
    setValue,
    getValues,
    watchPathArray,
    isDirty: isDirtyBasicParameters,
    isObjectLinkedValue,
    reset,
    handleSubmit,
    viewObjectCode,
    columnHeaderSettingsRef,
    columnTextSettingsRef,
    initialColumnHeaderStyles,
    initialColumnTextStyles,
    watchUseDisplayRule,
    watchDisplayRule,
    watchDisableEditable,
    watchActions,
    watchedDropDownListFilters,
    watchedDropDownWindowFilters,
    watchValue,
    methods,
    contextProps: props,
  })

  const { autocompleteOptionsDisplayRules, isLoadingDisplayRules } = data

  const {
    isTextParamsDirty,
    isShowResetConfirmModal,
    isShowScriptValueDialog,
    isShowFilterDialog,
    currentFilter,
    currentTypeFilter,
  } = state
  // TODO: Привести к нормальному виду хуков
  const {
    handleChangeValue,
    toggleOpenScriptValueDialog,
    handleChangeBinding,
    handleSave,
    handleTextExternalDirty,
    handleHeaderExternalDirty,
    handleSetValuesColumnStyles,
    handleClickReset,
    handleResetParams,
    handleSetShowResetConfirmModal,
    handleAddFilter,
    handleDoubleClickChip,
    handleDeleteFilter,
    handleSubmitFilter,
    handleCloseFilterDialog,
  } = handlers

  const isDirty = isDirtyBasicParameters || isTextParamsDirty

  usePrompt({ when: isDirty })

  const {
    columnInputs,
    valueTypeInputs,
    sortInputs,
    checkboxesInputs,
    dropwDownListFormInput,
    dropwDownWindowFormInput,
    preFillTypeInput,
    preFillStaticValueInput,
    formatDateInput,
  } = useInputs({
    watchBinding,
    watchValue,
    watchPathArray,
    dropdownEntities,
    handleChangeValue,
    valueOptions,
    bindTypeOptions,
    actionEventsOptions,
    handleChangeBinding,
    valueType,
    toggleOpenScriptValueDialog,
    isFieldRequired,
    fieldType,
    watchAsCheckbox,
    watchAsDuration,
    watchUseDisplayRule,
    entityType,
    autocompleteOptionsDisplayRules,
    isLoadingDisplayRules,
    props,
  })

  return {
    formatDateInput,
    isDirty,
    handleSave,
    isLoading,
    isShowScriptValueDialog,
    toggleOpenScriptValueDialog,
    methods,
    columnInputs,
    valueTypeInputs,
    sortInputs,
    preFillTypeInput,
    preFillStaticValueInput,
    checkboxesInputs,
    dropwDownListFormInput,
    dropwDownWindowFormInput,
    viewObjectCode,
    filteredObjectFields,
    columnHeaderSettingsRef,
    columnTextSettingsRef,
    initialColumnHeaderStyles,
    initialColumnTextStyles,
    watchUseParameters,
    watchBinding,
    isShowResetConfirmModal,
    isShowFilterDialog,
    currentFilter,
    currentTypeFilter,
    handleTextExternalDirty,
    handleHeaderExternalDirty,
    handleSetValuesColumnStyles,
    handleClickReset,
    handleResetParams,
    handleSetShowResetConfirmModal,
    handleAddFilter,
    handleDoubleClickChip,
    handleDeleteFilter,
    handleSubmitFilter,
    handleCloseFilterDialog,
    valueType,
    errors,
  }
}
